import type { Locale } from '@sjuni/types';
import { Button, Chip, Select } from '@sjuni/ui';
import { ImageOff, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { thumbUrl } from '@/lib/media/thumbUrl';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import { NewsEditor } from './NewsEditor';
import type { NewsCategoryRow, NewsRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/news`, `/news/new`, `/news/:id`.
 *
 * docs/09-BUILD-PLAN.md calls news "the most-used module — get it right first",
 * and the list is where most of the using happens: an editor opens this screen
 * far more often than any editor, so its filters are the module.
 */
export function NewsPage() {
  // The splat, not a named param — the router registers each module as
  // `{path}/*` so a detail route is gated by the module's permission before its
  // own screen decides what to do with the rest of the path.
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <NewsList /> : <NewsEditor recordId={rest} />;
}

function NewsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams();

  const [selected, setSelected] = useState<Set<number | string>>(new Set());
  const [deleting, setDeleting] = useState<NewsRow | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const { data, isPending, isError } = useResourceList<NewsRow>('news', list.params);

  /*
   * The categories, for the filter dropdown. `per_page: 100` rather than a
   * paged fetch: docs/03-DATA-MODEL.md §12 seeds six of them and a taxonomy
   * that outgrows a hundred entries is a different problem from this one.
   */
  const categories = useResourceList<NewsCategoryRow>('news-categories', {
    per_page: 100,
    sort: 'sort_order',
  });

  const act = useResourceAction<NewsRow>('news');

  const mayDelete = can('news.delete');

  const columns: Column<NewsRow>[] = [
    {
      // §4.6 opens the news column set with the cover thumb.
      key: 'cover',
      label: t('news.columns.cover'),
      render: (row) => <CoverThumb row={row} locale={locale} />,
    },
    {
      key: 'title',
      label: t('news.columns.title'),
      render: (row) => (
        <div className="min-w-48">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>

          {/*
            The two review flags, which read identically in a list unless they
            are labelled: one is a Faculty Admin waiting on an editor (§3), the
            other is the importer's doubt about a migrated row.
          */}
          <span className="mt-1 flex flex-wrap gap-1.5">
            {row.pending_review ? <Chip tone="gold">{t('news.flags.pendingReview')}</Chip> : null}
            {row.needs_review ? <Chip tone="gold">{t('news.flags.needsReview')}</Chip> : null}
            {row.is_featured ? <Chip tone="navy">{t('news.flags.featured')}</Chip> : null}
          </span>
        </div>
      ),
    },
    {
      key: 'category',
      label: t('news.columns.category'),
      render: (row) => (
        <span className="text-secondary">
          {row.category ? valueFor(row.category.name, locale) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('news.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      // CLAUDE.md rule 5 — what is missing is stated, never inferred.
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'published_at',
      label: t('news.columns.date'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <span className="whitespace-nowrap text-secondary">
          {row.published_at ? (
            <time dateTime={row.published_at}>
              {new Date(row.published_at).toLocaleDateString()}
            </time>
          ) : (
            '—'
          )}
        </span>
      ),
    },
    {
      key: 'view_count',
      label: t('news.columns.views'),
      sortable: true,
      align: 'end',
      render: (row) => row.view_count.toLocaleString(),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) => {
        if (row.deleted_at) {
          return (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                act.mutate({ id: row.id, action: 'restore' });
              }}
            >
              {t('list.restore')}
            </Button>
          );
        }

        // A Faculty Admin holds no `news.delete` at all (§3), so the button is
        // absent rather than present and always refused.
        return mayDelete ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleting(row);
            }}
          >
            {t('common.delete')}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.news')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('news.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        selectedCount={selected.size}
        bulkActions={
          mayDelete ? (
            <Button
              type="button"
              size="sm"
              variant="danger"
              iconLeft={<Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => setBulkDeleting(true)}
            >
              {t('common.delete')}
            </Button>
          ) : null
        }
        filters={
          <>
            <Select
              label={t('news.filters.status')}
              hideLabel
              value={String(list.params.status ?? '')}
              onChange={(event) => list.setFilter('status', event.target.value || null)}
              options={[
                { value: '', label: t('news.filters.anyStatus') },
                ...(['draft', 'scheduled', 'published', 'archived'] as const).map((status) => ({
                  value: status,
                  label: t(`editor.publishing.statuses.${status}`),
                })),
              ]}
            />

            <Select
              label={t('news.filters.category')}
              hideLabel
              value={String(list.params.category_id ?? '')}
              onChange={(event) => list.setFilter('category_id', event.target.value || null)}
              options={[
                { value: '', label: t('news.filters.anyCategory') },
                ...(categories.data?.data ?? []).map((category) => ({
                  value: String(category.id),
                  label: valueFor(category.name, locale),
                })),
              ]}
            />

            {/*
              §3's review queue, as a toggle rather than a third dropdown. It is
              the one filter with a workflow behind it: without it "submit for
              review" raises a flag nobody has a way to look for.
            */}
            <Button
              type="button"
              size="sm"
              variant={list.params.pending_review ? 'primary' : 'secondary'}
              aria-pressed={Boolean(list.params.pending_review)}
              onClick={() =>
                list.setFilter('pending_review', list.params.pending_review ? null : '1')
              }
            >
              {t('news.filters.pendingReview')}
            </Button>
          </>
        }
        primaryAction={
          can('news.create') ? (
            <Button
              type="button"
              size="sm"
              iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => navigate('/news/new')}
            >
              {t('news.new')}
            </Button>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={(row) => navigate(`/news/${row.id}`)}
        selected={mayDelete ? selected : undefined}
        onSelectedChange={mayDelete ? setSelected : undefined}
        emptyTitle={t('news.empty.title')}
        emptyBody={t('news.empty.body')}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('news.delete.title')}
        body={t('news.delete.body', { title: deleting ? valueFor(deleting.title, locale) : '' })}
        confirmLabel={t('common.delete')}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />

      <ConfirmDialog
        open={bulkDeleting}
        title={t('news.delete.bulkTitle')}
        body={t('news.delete.bulkBody', { count: selected.size })}
        confirmLabel={t('common.delete')}
        // §3 — "Destructive bulk actions (> 10 records) require typing the
        // record count to confirm." `ConfirmDialog` holds the threshold.
        count={selected.size}
        busy={act.isPending}
        onCancel={() => setBulkDeleting(false)}
        onConfirm={() => {
          /*
           * One request per row rather than a bulk endpoint. §6 does not define
           * one, and inventing `DELETE /admin/news` with a body would skip the
           * per-record Policy check that is the whole of §3's faculty scoping.
           */
          void Promise.allSettled(
            [...selected].map((id) => act.mutateAsync({ id, action: 'delete' })),
          ).then(() => {
            setSelected(new Set());
            setBulkDeleting(false);
          });
        }}
      />
    </div>
  );
}

/**
 * §4.6's cover thumb.
 *
 * Read from the `cover` media object rather than a resolved URL string: task
 * #71 moved images onto the media library and the API stopped sending
 * `cover_url`, but this went on asking for it — so every row fell into the
 * empty branch and the column showed a placeholder against 676 articles that
 * had a cover. It looked like content nobody had illustrated.
 *
 * The URL is still the server's to resolve; `thumbUrl` only picks which of the
 * conversions it sent to use.
 */
function CoverThumb({ row, locale }: { row: NewsRow; locale: Locale }) {
  const { t } = useTranslation();

  const src = thumbUrl(row.cover);

  if (!src) {
    return (
      <span
        className="grid h-10 w-16 place-items-center rounded-md bg-surface-alt text-muted"
        title={t('news.columns.noCover')}
      >
        <ImageOff size={14} strokeWidth={1.75} aria-hidden="true" />
        <span className="sr-only">{t('news.columns.noCover')}</span>
      </span>
    );
  }

  return (
    <img
      src={src}
      // The article's own title, so a screen reader hears which row this is
      // rather than "image". There is no stored `alt` until #71 adds one.
      alt={valueFor(row.title, locale)}
      loading="lazy"
      className="h-10 w-16 rounded-md object-cover"
    />
  );
}
